const FALLBACK_LOCALE = 'en-US';

/** Wallet amounts are whole units — balances and the reload increments are all integers. */
const WHOLE_NUMBER: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

/**
 * Repairs the HqO underscore form (`es_ES`) that some hosts send. Deliberately does NOT validate — a
 * malformed tag still reaches Intl, where the locale LIST below and the catch make it harmless.
 *
 * This is `normalizeLocale` from `@hqo/date-time/core`, inlined. The package cannot be imported here:
 * this repo is on react-scripts 4, whose webpack 4 does not support package `exports`, so
 * `@hqo/date-time/core` fails to resolve at BUILD time even though tsc and jest both resolve it — and
 * the package ROOT is not an option either, since it pulls Calendar → react-day-picker → @hqo/ui-atoms,
 * none of which this app has. Swap this for the real import when the wallet moves off CRA 4.
 */
const normalizeLocale = (locale?: string): string => {
  const hyphenated = (locale ?? '').trim().replace(/_/g, '-');

  return hyphenated || FALLBACK_LOCALE;
};

/**
 * Formats a wallet amount in the user's own locale.
 *
 * The locale is a plain BCP-47 tag, deliberately not a hand-maintained enum. It used to be typed to a
 * seven-value `LocaleEnum` while the app ships sixteen catalogues, and the call site narrowed to it
 * with `LocaleEnum[locale as keyof typeof LocaleEnum]` — which is `undefined` for the nine it omitted,
 * so those users silently formatted as `en-US` and read `€123` where their locale writes `123 €`. The
 * cast is what kept TypeScript quiet about it. Region decides where the symbol sits and which
 * separators are used, so the tag has to arrive intact.
 *
 * A locale LIST, never a bare tag: an unsupported tag passed alone resolves to the HOST's locale, so
 * `en-US` is named explicitly as the second choice.
 *
 * `currencyType` arrives from the API as a raw string, so an unrecognised code degrades to an
 * unlabelled number rather than the `RangeError` `Intl.NumberFormat` throws — that would take the whole
 * wallet view down. The fallback keeps the resolved locale, because a bad CURRENCY says nothing about
 * the user's number formatting: a German reading a broken code should still see `1.234`, not `1,234`.
 */
export const formatCurrency = (currencyValue: number, currencyType: string, locale?: string): string => {
  const locales = [normalizeLocale(locale), FALLBACK_LOCALE];

  try {
    return new Intl.NumberFormat(locales, { style: 'currency', currency: currencyType, ...WHOLE_NUMBER }).format(
      currencyValue,
    );
  } catch {
    try {
      return new Intl.NumberFormat(locales, WHOLE_NUMBER).format(currencyValue);
    } catch {
      return new Intl.NumberFormat(FALLBACK_LOCALE, WHOLE_NUMBER).format(currencyValue);
    }
  }
};
